import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { CATALOG_ASSEMBLY_TYPES, CATALOG_ORIENTATIONS } from "@njbsoft/api-client";
import { useCatalogAssemblyLookup } from "@/hooks/use-catalog-assembly-lookup";
import {
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  PageHeader,
  Skeleton,
} from "@/components/ui";

type CatalogAssemblyType = components["schemas"]["CatalogAssemblyType"];
type CatalogOrientation = components["schemas"]["CatalogOrientation"];
type AssemblyLookupResponse = components["schemas"]["AssemblyLookupResponse"];
type CatalogAssemblyDetail = components["schemas"]["CatalogAssemblyDetail"];

export const Route = createFileRoute("/_authenticated/catalog/lookup")({
  component: CatalogLookupPage,
});

interface FormState {
  assemblyType?: CatalogAssemblyType;
  manufacturerName: string;
  model: string;
  size: string;
  orientation?: CatalogOrientation;
}

const initialForm: FormState = {
  assemblyType: undefined,
  manufacturerName: "",
  model: "",
  size: "",
  orientation: undefined,
};

function CatalogLookupPage() {
  const { t } = useTranslation(["catalog", "common", "nav"]);

  // The form is split into "draft" (what the user is currently
  // typing) and "submitted" (the values driving the active query).
  // Submission copies draft → submitted; the query key is built
  // from submitted so transient edits don't churn the network.
  const [draft, setDraft] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState<FormState | null>(null);

  const sizeNumber = submitted?.size ? Number(submitted.size) : undefined;
  const lookup = useCatalogAssemblyLookup({
    assemblyType: submitted?.assemblyType,
    manufacturerName: submitted?.manufacturerName?.trim() || undefined,
    model: submitted?.model?.trim() || undefined,
    size: sizeNumber !== undefined && Number.isFinite(sizeNumber) ? sizeNumber : undefined,
    orientation: submitted?.orientation,
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitted({ ...draft });
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t("catalog.group", { ns: "nav" }) },
          { label: t("page.assembliesTitle"), to: "/catalog/assemblies" },
          { label: t("catalog:lookup.title", "Backflow assembly lookup") },
        ]}
      />
      <PageHeader title={t("catalog:lookup.title", "Backflow assembly lookup")} />
      <p className="text-sm text-muted">
        {t(
          "catalog:lookup.description",
          "Resolve a legacy assembly stamp to its current USC-approved canonical model.",
        )}
      </p>

      <Card>
        <CardHeader>{t("catalog:lookup.formTitle", "Assembly stamp lookup")}</CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-1">
              <span className="block text-sm font-medium text-on-surface">
                {t("catalog:lookup.fields.assemblyType", "Assembly type")}
              </span>
              <select
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-700 dark:text-white"
                value={draft.assemblyType ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    assemblyType: (e.target.value || undefined) as CatalogAssemblyType | undefined,
                  })
                }
                required
              >
                <option value="">{t("common:select", "Select...")}</option>
                {CATALOG_ASSEMBLY_TYPES.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="block text-sm font-medium text-on-surface">
                {t("catalog:lookup.fields.manufacturer", "Manufacturer")}
              </span>
              <input
                type="text"
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-700 dark:text-white"
                value={draft.manufacturerName}
                onChange={(e) => setDraft({ ...draft, manufacturerName: e.target.value })}
                placeholder={t("catalog:lookup.placeholders.manufacturer", "e.g. Watts")}
                required
              />
            </label>
            <label className="space-y-1">
              <span className="block text-sm font-medium text-on-surface">
                {t("catalog:lookup.fields.model", "Model")}
              </span>
              <input
                type="text"
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-700 dark:text-white"
                value={draft.model}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
                placeholder={t("catalog:lookup.placeholders.model", "e.g. 2000B-FP")}
                required
              />
            </label>
            <label className="space-y-1">
              <span className="block text-sm font-medium text-on-surface">
                {t("catalog:lookup.fields.size", "Size (inches)")}
              </span>
              <input
                type="number"
                step="0.001"
                min="0"
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-700 dark:text-white"
                value={draft.size}
                onChange={(e) => setDraft({ ...draft, size: e.target.value })}
                placeholder="1.0"
                required
              />
            </label>
            <label className="space-y-1">
              <span className="block text-sm font-medium text-on-surface">
                {t("catalog:lookup.fields.orientation", "Orientation (optional)")}
              </span>
              <select
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-700 dark:text-white"
                value={draft.orientation ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    orientation: (e.target.value || undefined) as CatalogOrientation | undefined,
                  })
                }
              >
                <option value="">{t("common:any", "Any")}</option>
                {CATALOG_ORIENTATIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-end md:col-span-2">
              <button
                type="submit"
                className="rounded bg-primary px-4 py-2 text-sm font-medium text-on-primary hover:opacity-90"
              >
                {t("catalog:lookup.submit", "Look up")}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      <LookupResults state={lookup} />
    </div>
  );
}

interface LookupResultsProps {
  state: ReturnType<typeof useCatalogAssemblyLookup>;
}

function LookupResults({ state }: LookupResultsProps) {
  const { t } = useTranslation(["catalog", "common"]);

  if (state.fetchStatus === "idle" && !state.data && !state.error) {
    return null; // form not yet submitted
  }
  if (state.isLoading) {
    return <Skeleton className="h-32" />;
  }
  if (state.error) {
    const status = (state.error as { status?: number }).status;
    if (status === 404) {
      return (
        <EmptyState
          title={t("catalog:lookup.notFound.title", "No catalog match")}
          description={t(
            "catalog:lookup.notFound.description",
            "No USC-approved assembly matches the supplied stamp. Verify the entry against the device label or check USC FCCCHR directly.",
          )}
        />
      );
    }
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-danger">
            {t("catalog:lookup.error", "Lookup failed: {{message}}", {
              message: (state.error as Error).message,
            })}
          </p>
        </CardContent>
      </Card>
    );
  }
  if (!state.data) return null;
  return <LookupResponseView data={state.data} />;
}

interface LookupResponseViewProps {
  data: AssemblyLookupResponse;
}

function LookupResponseView({ data }: LookupResponseViewProps) {
  const { t } = useTranslation(["catalog", "common"]);
  const {
    canonical_assemblies,
    former_designation_match,
    former_designation,
    unresolved_former_designation,
    unresolved_renamed_to_model,
  } = data;

  // Unresolved branch: alias matched but USC's renamed_to_model
  // can't be resolved to a current assembly. Surface a warning
  // rather than canonical cards.
  if (unresolved_former_designation) {
    return (
      <Card>
        <CardHeader>
          {t("catalog:lookup.unresolved.title", "USC published a model rename")}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-on-surface">
            {t(
              "catalog:lookup.unresolved.description",
              "USC published a model rename to {{model}} but we couldn't resolve it to a current approval. Verify with USC directly.",
              { model: unresolved_renamed_to_model ?? "(unknown)" },
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {former_designation_match && former_designation && canonical_assemblies.length === 1 && (
        <Card>
          <CardContent>
            <p className="text-sm text-on-surface">
              {t(
                "catalog:lookup.formerDesignation.banner",
                "USC formerly designated this assembly as {{legacy_model}}. Current designation: {{canonical_model}}. Source: {{source_note}}.",
                {
                  legacy_model: former_designation.legacy_model,
                  canonical_model: canonical_assemblies[0]?.model ?? "",
                  source_note: former_designation.source_note,
                },
              )}
            </p>
          </CardContent>
        </Card>
      )}
      {canonical_assemblies.map((a) => (
        <CanonicalCard key={a.id} assembly={a} />
      ))}
    </div>
  );
}

function CanonicalCard({ assembly }: { assembly: CatalogAssemblyDetail }) {
  const { t } = useTranslation(["catalog", "common"]);
  return (
    <Card>
      <CardHeader>
        {assembly.assembly_type} {assembly.manufacturer_name} {assembly.model} {assembly.size}
        &Prime; {assembly.orientation}
      </CardHeader>
      <CardContent>
        <Link
          to="/catalog/assemblies/$id"
          params={{ id: assembly.id }}
          className="text-sm text-primary hover:underline"
        >
          {t("catalog:lookup.view", "View canonical assembly")}
        </Link>
      </CardContent>
    </Card>
  );
}
